import { z } from 'zod';

/** The five per-meal kcal fields of the Daily Activity evening entry, in eating order. */
export const NUTRITION_MEAL_SLOTS = [
  'breakfast',
  'morning_snack',
  'lunch',
  'afternoon_snack',
  'dinner',
] as const;

export const UiNutritionMealSlotKeySchema = z.enum(NUTRITION_MEAL_SLOTS);

/**
 * One meal slot over the period. A slot is "eaten" on a day when its kcal is
 * above 0; `avgKcal` is over those days only, `sharePct` is the slot's part
 * of all kcal in the period.
 */
export const UiNutritionMealSlotSchema = z.object({
  key: UiNutritionMealSlotKeySchema,
  daysEaten: z.number(),
  totalKcal: z.number(),
  avgKcal: z.number().nullable(),
  sharePct: z.number().nullable(),
});

/**
 * One elapsed day of the period: each meal's kcal in eating order (`null` =
 * the field was not filled in), their sum (`null` = no meal field at all, i.e.
 * the day is not logged), the meals eaten (kcal above 0) and the water.
 */
export const UiNutritionDaySchema = z.object({
  /** YYYY-MM-DD */
  date: z.string(),
  slotKcal: z.array(z.number().nullable()),
  kcal: z.number().nullable(),
  meals: z.number(),
  waterL: z.number().nullable(),
});

/**
 * Client nutrition statistics. Calories are the sum of the five evening meal
 * fields (the `kcal_intake` definition) — the structured food diary does not
 * feed them. A day is "logged" when it has at least one meal field (0
 * included); a meal counts when its kcal is above 0, so there are at most
 * five a day. Every `null` means "nothing logged", never zero.
 */
export const UiNutritionStatisticsSchema = z.object({
  /** sample-size denominator: days of the period that already happened */
  daysInPeriod: z.number(),
  loggedDays: z.number(),
  totalKcal: z.number().nullable(),
  avgKcalPerDay: z.number().nullable(),
  meals: z.number(),
  kcalPerMeal: z.number().nullable(),
  mealsPerDay: z.number().nullable(),
  water: z.object({
    avgL: z.number().nullable(),
    days: z.number(),
  }),
  slots: z.array(UiNutritionMealSlotSchema),
  /** elapsed days of the period, oldest first */
  days: z.array(UiNutritionDaySchema),
});

export type UiNutritionMealSlotKey = z.infer<
  typeof UiNutritionMealSlotKeySchema
>;
export type UiNutritionMealSlot = z.infer<typeof UiNutritionMealSlotSchema>;
export type UiNutritionDay = z.infer<typeof UiNutritionDaySchema>;
export type UiNutritionStatistics = z.infer<typeof UiNutritionStatisticsSchema>;
